import { Typography } from '@mui/material';
import type { ProductCategory } from '@partful/shared-types';
import { useTranslation } from 'react-i18next';
import SearchableDropdown from 'src/components/SearchableDropdown';
import { Toast } from 'src/components/Toast';
import { useProductCategoriesMutation } from 'src/hooks/useProductCategoriesMutation';
import { useConfiguration } from 'src/providers/Configuration';
import { useUser } from 'src/providers/User';
import { productCategoryPathEnumerationSeparator } from 'src/utils/urls';

function transformBackendCategoriesToFrontend(option: string) {
  const lastPathSeparator = option.lastIndexOf(productCategoryPathEnumerationSeparator);
  const parents = option.substring(0, lastPathSeparator);
  const leaf = option.substring(lastPathSeparator + 1, option.length);

  if (lastPathSeparator < 0) {
    return {
      value: option,
      label: leaf,
    };
  }

  return {
    value: option,
    label: leaf,
    group: parents,
  };
}

function generateProductCategoriesEnumerations(node: ProductCategory, parentName: string): any[] {
  const currentName = parentName || node.name;
  return node?.children?.length
    ? node.children.map((child: any) =>
        child?.children?.length
          ? generateProductCategoriesEnumerations(child, currentName.concat(`.${child.name}`))
          : currentName.concat(`.${child.name}`)
      )
    : [node.name];
}

interface ProductCategoriesProps {
  configurationPID: string;
}

export function ProductCategories({ configurationPID }: Readonly<ProductCategoriesProps>) {
  const { productCategories } = useUser();
  const { productCategories: initialProductCategories } = useConfiguration();

  const { t } = useTranslation('manageProduct');

  const {
    selectedProductCategories,
    setSelectedProductCategories,
    isLoading: isLoadingMutateCategories,
    isError: isErrorMutateCategories,
    handleProductCategoriesChange,
  } = useProductCategoriesMutation(
    configurationPID,
    initialProductCategories?.map(transformBackendCategoriesToFrontend)
  );

  // this transforms the product categories in the recursive format ProductCategories from '@samsonvt/shared-types/accountsTable';
  // to OptionsWithLabelAndGroup flat format. This is done to show categories with groupings and only show the leaf category name in the dropdown
  const options =
    /* istanbul ignore next */
    productCategories
      ?.map((category) => generateProductCategoriesEnumerations(category, ''))
      .flat(Infinity)
      .map(transformBackendCategoriesToFrontend) || [];

  if (productCategories === undefined) {
    /* istanbul ignore next */
    return null;
  }

  return (
    <>
      <SearchableDropdown
        loading={isLoadingMutateCategories}
        value={selectedProductCategories}
        options={options}
        setValue={async (newValue) => {
          await handleProductCategoriesChange(newValue);
          setSelectedProductCategories(newValue);
        }}
        label={t('productOverview.categories')}
        placeholder={t('productOverview.categoriesPlaceholder')}
        fullWidth
        multiple
      />
      <Typography>{t('productOverview.manageCategories')}</Typography>
      <Toast
        dependency={isErrorMutateCategories}
        severity="error"
        title={t('productOverview.errorUpdatingProductCategories')}
      />
    </>
  );
}
